/**
 * Gestor de modales para el sistema de impresión
 */
class ModalManager {
    constructor(printModule) {
        this.printModule = printModule;
    }

    /**
     * Mostrar modal usando Bootstrap
     */
    showModal(modal) {
        // Si Bootstrap 5 está disponible
        if (typeof bootstrap !== 'undefined' && window.bootstrap.Modal) {
            const bsModal = new window.bootstrap.Modal(modal);
            bsModal.show();
        }
        // Si Bootstrap 4 está disponible
        else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
            const bsModal = new window.bootstrap.Modal(modal);
            bsModal.show();
        }
        // Fallback manual
        else {
            modal.style.display = 'block';
            modal.classList.add('show');
            modal.setAttribute('aria-hidden', 'false');
            modal.setAttribute('aria-modal', 'true');

            // Agregar backdrop
            const backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = 'modal-backdrop-' + modal.id;
            document.body.appendChild(backdrop);

            // Agregar clase al body
            document.body.classList.add('modal-open');
        }
    }

    /**
     * Ocultar modal usando Bootstrap
     */
    hideModal(modal) {
        // Si Bootstrap 5 está disponible
        if (typeof bootstrap !== 'undefined' && window.bootstrap.Modal) {
            const bsModal = window.bootstrap.Modal.getInstance(modal);
            if (bsModal) bsModal.hide();
        }
        // Si Bootstrap 4 está disponible  
        else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
            const bsModal = window.bootstrap.Modal.getInstance(modal);
            if (bsModal) bsModal.hide();
        }
        // Fallback manual
        else {
            modal.style.display = 'none';
            modal.classList.remove('show');
            modal.setAttribute('aria-hidden', 'true');
            modal.removeAttribute('aria-modal');

            // Remover backdrop
            const backdrop = document.getElementById('modal-backdrop-' + modal.id);
            if (backdrop) backdrop.remove();

            // Remover clase del body
            document.body.classList.remove('modal-open');

            // Remover modal del DOM
            modal.remove();
        }
    }

    /**
     * Mostrar diálogo de configuración de impresión
     */
    showPrintDialog() {
        const modal = this.createPrintModal();
        document.body.appendChild(modal);
        this.showModal(modal);
    }

    /**
     * ✅ MODAL UNIVERSAL - SE ADAPTA AL TIPO DE REPORTE
     */
    createPrintModal() {
        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.id = 'printReportModal';
        modal.setAttribute('tabindex', '-1');

        const currentDate = new Date().toLocaleDateString();

        // ✅ DETECTAR TIPO DE REPORTE AUTOMÁTICAMENTE
        const reportType = this.printModule.detectReportType();
        const modalTitle = this.printModule.getModalTitle(reportType);
        const defaultTitle = this.printModule.getDefaultTitle(reportType, currentDate);
        const onlyTableInclude = this.printModule.options.onlyTableExport;

        modal.innerHTML = `
            <div class="modal-dialog modal-md" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title" id="printReportModalLabel">
                            <i class="fas fa-print"></i> 
                            Configuración de Impresión - ${modalTitle}
                        </h4>
                        <button type="button" class="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="form-group mb-3">
                            <label for="print-title">Título del reporte:</label>
                            <input type="text" class="form-control" id="print-title" 
                                   value="${defaultTitle}">
                        </div>
                        
                        <div class="form-group mb-3">
                            <label for="print-orientation">Orientación:</label>
                            <select class="form-control" id="print-orientation">
                                <option value="landscape" ${this.printModule.options.pdfOrientation === 'landscape' ? 'selected' : ''}>
                                    Horizontal ${this.printModule.isRecommendedLandscape(reportType) ? '(Recomendado)' : ''}
                                </option>
                                <option value="portrait" ${this.printModule.options.pdfOrientation === 'portrait' ? 'selected' : ''}>
                                    Vertical ${this.printModule.isRecommendedPortrait(reportType) ? '(Recomendado)' : ''}
                                </option>
                            </select>
                        </div>
                        
                        <div class="form-group mb-3">
                            <label>Contenido a incluir:</label>
                            <div class="checkbox-group">
                                <div class="form-check" ${onlyTableInclude ? 'hidden' : ''}>
                                    <input type="checkbox" class="form-check-input" id="include-charts" ${onlyTableInclude ? '' : 'checked'}>
                                    <label class="form-check-label" for="include-charts">
                                        ${this.printModule.getChartsLabel(reportType)}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input type="checkbox" class="form-check-input" id="include-table" checked>
                                    <label class="form-check-label" for="include-table">
                                        ${this.printModule.getTableLabel(reportType)}
                                    </label>
                                </div>
                            </div>
                        </div>
                        
                        <div class="form-group">
                            <label for="print-notes">Comentarios adicionales (opcional):</label>
                            <textarea class="form-control" id="print-notes" rows="3" 
                                      placeholder="Agregue comentarios que aparecerán en el reporte..."></textarea>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" id="cancel-print-btn">
                            <i class="fas fa-times"></i> Cancelar
                        </button>
                        <button type="button" class="btn btn-primary" id="start-print-btn">
                            <i class="fas fa-file-pdf"></i> Generar PDF
                        </button>
                    </div>
                </div>
            </div>
        `;

        this.setupPrintModalEvents(modal);
        return modal;
    }

    /**
     * Configurar eventos del modal de impresión
     */
    setupPrintModalEvents(modal) {
        const printBtn = modal.querySelector('#start-print-btn');
        const cancelBtn = modal.querySelector('#cancel-print-btn');
        const closeBtn = modal.querySelector('.close');

        // Evento para generar PDF
        printBtn.addEventListener('click', async () => {
            try {
                if (window['guardarBitacoraPrint']) {
                    try {
                        window['guardarBitacoraPrint']();
                    } catch (error) {

                    }
                }
                console.log('🚀 Iniciando generación de PDF...');

                const config = this.printModule.getPrintConfiguration(modal);

                if (!this.printModule.validatePrintConfig(config)) {
                    return;
                }

                this.hideModal(modal);

                // ✅ USAR EL MÉTODO QUE FUNCIONABA ANTES
                await this.printModule.startPrintProcess(config);

                // ❌ COMENTAR EL MÉTODO PROBLEMÁTICO POR AHORA
                // await this.printModule.generateReportWithValidation(config);

                console.log('✅ PDF generado exitosamente, preparando preview...');

            } catch (error) {
                console.error('❌ Error en generación de PDF:', error);
                this.printModule.hideLoading();
                this.printModule.showError(`Error al generar PDF: ${error.message}`);
            }
        });

        // Eventos para cerrar modal
        [cancelBtn, closeBtn].forEach(btn => {
            btn.addEventListener('click', () => {
                this.hideModal(modal);
            });
        });

        // Cerrar al hacer click en el backdrop
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.hideModal(modal);
            }
        });

        // Cerrar con ESC
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('show')) {
                this.hideModal(modal);
            }
        });
    }

    /**
     * Crear modal de preview del PDF
     */
    createPreviewModal(pdfBlob, config) {
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.id = 'printPreviewModal';
        modal.setAttribute('tabindex', '-1');
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-hidden', 'true');

        modal.innerHTML = `
            <div class="modal-dialog modal-xl" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">
                            <i class="fas fa-eye"></i> Vista Previa - ${config.title}
                        </h4>
                        <button type="button" class="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body" style="height: 80vh; overflow: hidden;">
                        <iframe src="${pdfUrl}" width="100%" height="100%" style="border: none;"></iframe>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" id="close-preview-btn">
                            <i class="fas fa-times"></i> Cerrar
                        </button>
                        <button type="button" class="btn btn-info" id="share-pdf-btn">
                            <i class="fas fa-share-alt"></i> Compartir
                        </button>
                        <button type="button" class="btn btn-primary" id="download-pdf-btn">
                            <i class="fas fa-download"></i> Descargar PDF
                        </button>
                        <button type="button" class="btn btn-success" id="print-pdf-btn">
                            <i class="fas fa-print"></i> Imprimir
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Guardar referencias en el módulo principal
        this.printModule.currentPdfBlob = pdfBlob;
        this.printModule.currentPdfUrl = pdfUrl;

        // Configurar eventos del modal de preview
        this.setupPreviewModalEvents(modal, pdfUrl);

        return modal;
    }

    /**
     * Configurar eventos del modal de preview
     */
    setupPreviewModalEvents(modal, pdfUrl) {
        const closeBtn = modal.querySelector('#close-preview-btn');
        const downloadBtn = modal.querySelector('#download-pdf-btn');
        const printBtn = modal.querySelector('#print-pdf-btn');
        const shareBtn = modal.querySelector('#share-pdf-btn');
        const closeIcon = modal.querySelector('.close');

        // ✅ VERIFICAR QUE LOS ELEMENTOS EXISTEN
        if (!downloadBtn || !printBtn || !shareBtn) {
            console.error('❌ No se encontraron todos los botones del modal');
            return;
        }

        // ✅ ESTABLECER PDF EN SHAREMANAGER INMEDIATAMENTE
        if (this.printModule.currentPdfBlob && this.printModule.shareManager) {
            const setPdfResult = this.printModule.shareManager.setPDF(this.printModule.currentPdfBlob);
            if (setPdfResult) {
                console.log('✅ PDF establecido en ShareManager');
                console.log('🔍 ShareManager debug:', this.printModule.shareManager.getDebugInfo());
            } else {
                console.error('❌ Error estableciendo PDF en ShareManager');
            }
        }

        // Eventos para cerrar
        [closeBtn, closeIcon].forEach(btn => {
            if (btn) {
                btn.addEventListener('click', () => {
                    try {
                        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                        this.hideModal(modal);
                    } catch (error) {
                        console.error('Error al cerrar modal:', error);
                    }
                });
            }
        });

        // ✅ EVENTO PARA DESCARGAR
        downloadBtn.addEventListener('click', (e) => {
            e.preventDefault();
            console.log('🔽 Clic en descargar PDF...');
            this.printModule.downloadPDF();
        });

        // ✅ EVENTO PARA IMPRIMIR
        printBtn.addEventListener('click', (e) => {
            e.preventDefault();
            console.log('🖨️ Clic en imprimir PDF...');
            this.printModule.printPDF();
        });

        // ✅ EVENTO PARA COMPARTIR
        shareBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            console.log('📤 Clic en compartir PDF...');
            await this.printModule.sharePDF();
        });

        // Cerrar al hacer click en el backdrop
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                try {
                    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                    this.hideModal(modal);
                } catch (error) {
                    console.error('Error al cerrar modal:', error);
                }
            }
        });

        console.log('✅ Eventos del modal configurados correctamente');
    }

    /**
     * ✅ MODAL DE OPCIONES DE COMPARTIR
     */
    showShareOptionsModal(fileName, title, shareText) {
        const pdfUrl = URL.createObjectURL(this.printModule.currentPdfBlob);

        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.id = 'shareOptionsModal';
        modal.setAttribute('tabindex', '-1');

        modal.innerHTML = `
            <div class="modal-dialog modal-md">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">
                            <i class="fas fa-share-alt"></i> Compartir Reporte PDF
                        </h4>
                        <button type="button" class="close" data-dismiss="modal">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="text-center mb-3">
                            <h5>${title}</h5>
                            <p class="text-muted">Generado: ${new Date().toLocaleString()}</p>
                        </div>
                        
                        <div class="list-group">
                            <a href="#" class="list-group-item list-group-item-action" id="share-whatsapp">
                                <i class="fab fa-whatsapp text-success"></i>
                                <strong>WhatsApp</strong>
                                <small class="text-muted d-block">Compartir información por WhatsApp</small>
                            </a>
                            
                            <a href="#" class="list-group-item list-group-item-action" id="share-email">
                                <i class="fas fa-envelope text-primary"></i>
                                <strong>Email</strong>
                                <small class="text-muted d-block">Enviar por correo electrónico</small>
                            </a>
                            
                            <a href="#" class="list-group-item list-group-item-action" id="share-copy">
                                <i class="fas fa-copy text-info"></i>
                                <strong>Copiar información</strong>
                                <small class="text-muted d-block">Copiar texto al portapapeles</small>
                            </a>
                            
                            <a href="${pdfUrl}" download="${fileName}" class="list-group-item list-group-item-action">
                                <i class="fas fa-download text-warning"></i>
                                <strong>Descargar PDF</strong>
                                <small class="text-muted d-block">Guardar archivo en dispositivo</small>
                            </a>
                            
                            <a href="#" class="list-group-item list-group-item-action" id="share-link">
                                <i class="fas fa-link text-secondary"></i>
                                <strong>Abrir en nueva ventana</strong>
                                <small class="text-muted d-block">Ver PDF en nueva pestaña</small>
                            </a>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">
                            <i class="fas fa-times"></i> Cerrar
                        </button>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);
        this.showModal(modal);

        // ✅ CONFIGURAR EVENTOS
        this.setupShareModalEvents(modal, shareText, pdfUrl, fileName);
    }

    /**
     * ✅ CONFIGURAR EVENTOS DEL MODAL DE COMPARTIR
     */
    setupShareModalEvents(modal, shareText, pdfUrl, fileName) {
        // WhatsApp
        modal.querySelector('#share-whatsapp').addEventListener('click', (e) => {
            e.preventDefault();
            if (this.printModule.shareManager) {
                this.printModule.shareManager.shareWhatsApp(shareText);
            } else {
                const whatsappText = encodeURIComponent(shareText);
                window.open(`https://wa.me/?text=${whatsappText}`, '_blank');
            }
            this.hideModal(modal);
        });

        // Email
        modal.querySelector('#share-email').addEventListener('click', (e) => {
            e.preventDefault();
            if (this.printModule.shareManager) {
                this.printModule.shareManager.shareEmail(fileName, shareText);
            } else {
                const subject = encodeURIComponent('Reporte - Quro GPS');
                const body = encodeURIComponent(`${shareText}\n\n📎 NOTA: Descargue el PDF y adjúntelo manualmente al email.\nArchivo: ${fileName}\n\nSaludos,\nSistema Quro GPS`);
                window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
            }
            this.hideModal(modal);
        });

        // Copiar
        modal.querySelector('#share-copy').addEventListener('click', async (e) => {
            e.preventDefault();
            try {
                if (this.printModule.shareManager) {
                    const success = await this.printModule.shareManager.copyToClipboard(shareText);
                    if (success) {
                        this.printModule.showSuccess('Información copiada al portapapeles');
                    } else {
                        this.printModule.showError('No se pudo copiar al portapapeles');
                    }
                } else {
                    // Fallback
                    if (navigator.clipboard) {
                        await navigator.clipboard.writeText(shareText);
                    } else {
                        const textArea = document.createElement('textarea');
                        textArea.value = shareText;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                    }
                    this.printModule.showSuccess('Información copiada al portapapeles');
                }
            } catch (error) {
                console.error('Error copiando:', error);
                this.printModule.showError('No se pudo copiar al portapapeles');
            }
            this.hideModal(modal);
        });

        // Abrir en nueva ventana
        modal.querySelector('#share-link').addEventListener('click', (e) => {
            e.preventDefault();
            if (this.printModule.shareManager) {
                this.printModule.shareManager.openInNewWindow();
            } else {
                window.open(pdfUrl, '_blank');
            }
            this.hideModal(modal);
        });

        // Limpiar URLs al cerrar
        modal.addEventListener('hidden.bs.modal', () => {
            URL.revokeObjectURL(pdfUrl);
            modal.remove();
        });
    }

    /**
     * Mostrar preview del PDF generado
     */
    showPrintPreview(pdfBlob, config) {
        const modal = this.createPreviewModal(pdfBlob, config);
        document.body.appendChild(modal);
        this.showModal(modal);
    }
}

export default ModalManager;